import type { BundledMcpTemplate } from '@/features/integrations/bundled-mcp-templates';
import type {
  McpPermission,
  OnDeviceMcpCatalogEntry,
} from '@/features/integrations/integrations.types';

import type {
  ServerSetup,
  ServerTransport,
} from '@littlebird/axon-mcp/contract';

/**
 * A server that connects without the user filling anything in, so the
 * connect dialog can be a single button instead of the full add-server form.
 */
export interface OneClickServer {
  /** The template id the saved entry is tagged with. */
  id: string;
  name: string;
  description: string;
  transport: ServerTransport;
  setup?: ServerSetup;
  requiredPermissions: readonly McpPermission[];
  /** Informational lines shown above the Connect button. */
  notes: readonly string[];
  capabilities: readonly string[];
}

/** Catalog packages with nothing to fill in: no arguments, env, or requirements. */
export function isOneClickCatalogEntry(
  entry: OnDeviceMcpCatalogEntry
): boolean {
  return (
    entry.arg_hints.length === 0 &&
    entry.env_hints.length === 0 &&
    entry.requirements.length === 0
  );
}

export function oneClickServerFromCatalog(
  entry: OnDeviceMcpCatalogEntry
): OneClickServer {
  return {
    id: entry.id,
    name: entry.name,
    description: entry.description,
    transport: {
      kind: 'stdio',
      runtime: entry.runtime,
      packageSpec: entry.package_spec,
    },
    requiredPermissions: entry.required_permissions,
    notes: entry.setup_instructions,
    capabilities: [],
  };
}

export function oneClickServerFromBundled(
  template: BundledMcpTemplate
): OneClickServer {
  return {
    id: template.id,
    name: template.name,
    description: template.description,
    transport: template.transport,
    ...(template.setup && { setup: template.setup }),
    requiredPermissions: template.requiredPermissions ?? [],
    notes: template.deviceNote ?? [],
    capabilities: template.capabilities ?? [],
  };
}
